import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse, User } from '@/types';
import { mergeUserClaimsFromToken } from '@/utils/jwtPermissions';
import { API_BASE_URL } from '@/config/apiBaseUrl';
import { getLoginEntryPath } from '@/utils/safeReturnUrl';

const API_URL = API_BASE_URL;

export type RefreshedTokenPair = { token: string; refreshToken: string };

/** Single in-flight refresh so parallel 401s and initializeAuth share one POST /auth/refresh-token. */
let refreshInFlight: Promise<RefreshedTokenPair> | null = null;

export function performTokenRefresh(): Promise<RefreshedTokenPair> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async (): Promise<RefreshedTokenPair> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }
    const refreshResponse = await axios.post(
      `${API_URL}/auth/refresh-token`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    const responseData = refreshResponse.data as {
      data?: { token?: string; refreshToken?: string };
      token?: string;
      refreshToken?: string;
    };
    const { token, refreshToken: newRefreshToken } = responseData.data || responseData;
    if (!token) {
      throw new Error('No token received from refresh');
    }
    const nextRt = newRefreshToken || refreshToken;
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', nextRt);

    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const merged = mergeUserClaimsFromToken(JSON.parse(userStr) as User, token);
        localStorage.setItem('user', JSON.stringify(merged));
        const { useAuthStore } = await import('@/store/authStore');
        const st = useAuthStore.getState();
        if (st.user?.id === merged.id) {
          st.setUser(merged);
        }
      }
    } catch (e) {
      console.warn('[API] Could not sync user permissions after token refresh', e);
    }

    return { token, refreshToken: nextRt };
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Finance GL reports (trial balance, P&L, etc.) are slow on large data; default 30s is too low.
        if (String(config.url ?? '').includes('/finance/reports/')) {
          config.timeout = Math.max(config.timeout ?? 0, 300_000);
        }
        if (String(config.url ?? '').includes('/opening-stock/batches/')) {
          const path = String(config.url ?? '');
          if (path.includes('/validate') || path.includes('/post') || path.includes('/upload')) {
            config.timeout = Math.max(config.timeout ?? 0, 300_000);
          }
        }
        if (String(config.url ?? '').includes('/hr/employees/import')) {
          config.timeout = Math.max(config.timeout ?? 0, 300_000);
        }
        // Full item master can exceed 30s on large tenants; pickers should also pass limit=.
        if (String(config.url ?? '').includes('/inventory/items') && String(config.method ?? 'get').toLowerCase() === 'get') {
          config.timeout = Math.max(config.timeout ?? 0, 120_000);
        }
        // Platform tenant provisioning installs packs + seeds synchronously and can exceed 30s.
        if (
          String(config.url ?? '').includes('/platform/tenants/') &&
          (String(config.url ?? '').includes('/provision') ||
            String(config.url ?? '').includes('/diagnostics'))
        ) {
          config.timeout = Math.max(config.timeout ?? 0, 300_000);
        }
        // When sending FormData, do not use application/json so axios can set multipart/form-data with boundary
        if (config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
          const headers = config.headers as Record<string, unknown>;
          delete headers['Content-Type'];
          delete headers['content-type'];
        }
        // If Authorization header is already set (e.g., from token refresh retry), use it
        // Otherwise, read from localStorage
        if (!config.headers.Authorization) {
          const token = localStorage.getItem('token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } else {
          // Log when we're using an existing Authorization header (from retry)
          if (config._tokenRefreshed) {
            console.log('[API Interceptor] Using refreshed token from retry', {
              url: config.url,
              hasAuthHeader: !!config.headers.Authorization
            });
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle token expiration - prevent infinite loops
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest._retryCount) {
          originalRequest._retry = true;
          originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

          // Prevent infinite retry loops - max 2 retries
          if (originalRequest._retryCount > 2) {
            console.error('[API Interceptor] Max retry attempts reached, redirecting to login');
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = getLoginEntryPath();
            return Promise.reject(error);
          }

          try {
            const rt = localStorage.getItem('refreshToken');
            if (rt) {
              console.log('[API Interceptor] Attempting to refresh token...');
              const { token } = await performTokenRefresh();

              const retryConfig = {
                ...originalRequest,
                headers: {
                  ...originalRequest.headers,
                  Authorization: `Bearer ${token}`
                },
                _tokenRefreshed: true,
                _retry: true,
                _retryCount: originalRequest._retryCount
              };

              return this.client(retryConfig);
            } else {
              console.log('[API Interceptor] No refresh token available, redirecting to login');
              // No refresh token available, clear everything
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              window.location.href = getLoginEntryPath();
            }
          } catch (refreshError: unknown) {
            const status = (refreshError as { response?: { status?: number } })?.response?.status;
            if (status === 429) {
              console.warn('[API Interceptor] Refresh rate-limited (429); keeping session, not redirecting to login');
              return Promise.reject(refreshError);
            }
            console.error('[API Interceptor] Refresh token failed, logging out:', refreshError);
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = getLoginEntryPath();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  getClient(): AxiosInstance {
    return this.client;
  }
}

export default new ApiService();

